import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Loader2 } from "lucide-react";
import { formatApiError } from "@/lib/api";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await login(email, password);
      navigate("/");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" style={{ background: "var(--bg-page)" }}>
      {/* Left: agriculture brand panel */}
      <div className="hidden lg:flex w-1/2 relative overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1560493676-04071c5f467b?crop=entropy&cs=srgb&fm=jpg&q=85"
          alt="Farm field"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0" style={{ background: "linear-gradient(135deg, rgba(27,67,50,0.85), rgba(27,67,50,0.55))" }} />
        <div className="relative z-10 flex flex-col justify-between p-12 text-white w-full">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-lg overflow-hidden bg-black flex items-center justify-center">
              <img src="/navaratnaa-logo.png" alt="Navaratnaa" className="w-full h-full object-contain" />
            </div>
            <div>
              <div className="font-heading font-bold text-lg leading-none">Navaratnaa</div>
              <div className="text-[10px] tracking-[0.25em] uppercase opacity-80 mt-1">Cropscience HRMS</div>
            </div>
          </div>
          <div>
            <div className="text-xs tracking-[0.3em] uppercase opacity-80 mb-4">Cultivating People · Growing Business</div>
            <h1 className="font-heading text-4xl sm:text-5xl font-bold leading-tight tracking-tight">
              Manage your <span style={{ color: "#FADED7" }}>workforce</span><br />
              from <span style={{ color: "#D8F3DC" }}>seed to harvest</span>.
            </h1>
            <p className="mt-6 opacity-90 max-w-md leading-relaxed">
              A complete HRMS for agriculture companies — employees, attendance, payroll,
              TA/DA, assets, and performance, all in one organic dashboard.
            </p>
          </div>
          <div className="text-xs opacity-70">© {new Date().getFullYear()} Navaratnaa Cropscience Pvt. Ltd.</div>
        </div>
      </div>

      {/* Right: form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <div className="w-11 h-11 rounded-lg overflow-hidden bg-black flex items-center justify-center">
              <img src="/navaratnaa-logo.png" alt="Navaratnaa" className="w-full h-full object-contain" />
            </div>
            <div className="font-heading font-bold text-lg" style={{ color: "var(--color-primary)" }}>Navaratnaa HRMS</div>
          </div>
          <div className="text-xs tracking-[0.3em] uppercase font-semibold mb-3" style={{ color: "var(--color-secondary)" }}>Welcome back</div>
          <h2 className="font-heading text-3xl font-bold tracking-tight mb-2">Sign in to your account</h2>
          <p className="text-stone-600 mb-8 text-sm">Access the workforce management workspace.</p>

          <form onSubmit={submit} className="space-y-4" data-testid="login-form">
            <div>
              <label className="kpi-label block mb-2">Email</label>
              <input
                data-testid="login-email-input"
                type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-2.5 rounded-md border border-stone-300 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition"
                placeholder="you@navaratnaa.com"
              />
            </div>
            <div>
              <label className="kpi-label block mb-2">Password</label>
              <input
                data-testid="login-password-input"
                type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-2.5 rounded-md border border-stone-300 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition"
                placeholder="••••••••"
              />
            </div>
            {error && (
              <div className="text-sm px-3 py-2 rounded-md" data-testid="login-error"
                   style={{ background: "#FADED7", color: "#9A3412" }}>{error}</div>
            )}
            <button
              type="submit" disabled={loading} data-testid="login-submit-button"
              className="w-full py-2.5 rounded-md font-semibold text-white transition-all active:scale-[0.98] disabled:opacity-60 flex items-center justify-center gap-2"
              style={{ background: "var(--color-primary)" }}
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              Sign In
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
